import isEmpty from 'lodash/isEmpty';
import { IRange } from '../../../types';
import { IDateRangeConfigOptions } from './types';

export type ExclusiveDateRange =
  | 'today'
  | 'previous-session'
  | 'previous-5-sessions'
  | 'previous-10-sessions'
  | 'previous-15-sessions'
  | 'previous-20-sessions'
  | 'all-time'
  | 'custom'
  | 'two-date';

export type InclusiveDateRange =
  | 'today'
  | 'last-session'
  | 'last-5-sessions'
  | 'last-10-sessions'
  | 'last-15-sessions'
  | 'last-20-sessions'
  | 'all-time'
  | 'custom'
  | 'two-date';

export interface IInclusiveDateOption {
  label: string;
  value: InclusiveDateRange;
}

export interface IExclusiveDateOption {
  label: string;
  value: ExclusiveDateRange;
}

export type DateRangeTypeOption = IInclusiveDateOption | IExclusiveDateOption;

export interface IDateRange {
  dateType: DateRangeTypeOption;
  dateRange?: IRange;
}

/**
 * @description the available inclusive date range options
 */
const defaultInclusiveOptions: IInclusiveDateOption[] = [
  { label: 'Last Session', value: 'last-session' }, // order by date desc, limit one
  { label: 'Last 5 Sessions', value: 'last-5-sessions' }, // order by date desc, limit 5
  { label: 'Last 10 Sessions', value: 'last-10-sessions' }, // order by date desc, limit 10
  { label: 'Last 15 Sessions', value: 'last-15-sessions' }, // order by date desc, limit 15
  { label: 'Last 20 Sessions', value: 'last-20-sessions' }, // order by date desc, limit 20
  { label: 'All Time', value: 'all-time' }, // all time
  { label: 'Custom Range', value: 'custom' }, // range query
];

/**
 * @description the available inclusive comparison range options
 */
const defaultExclusiveOptions: IExclusiveDateOption[] = [
  { label: 'Previous Session', value: 'previous-session' },
  { label: 'Previous 5 Sessions', value: 'previous-5-sessions' },
  { label: 'Previous 10 Sessions', value: 'previous-10-sessions' },
  { label: 'Previous 15 Sessions', value: 'previous-15-sessions' },
  { label: 'Previous 20 Sessions', value: 'previous-20-sessions' },
  { label: 'All Time', value: 'all-time' },
  { label: 'Custom Range', value: 'custom' },
];

/**
 * @description available inclusive date range options to use in date selects
 * @param options
 */
export const inclusiveDateRangeProvider = (
  options?: IDateRangeConfigOptions
): IInclusiveDateOption[] => {
  return !isEmpty(options)
    ? defaultInclusiveOptions.filter((opt: IInclusiveDateOption): boolean => {
        /// default allow
        let isAllowed: boolean = true;
        if (options?.disallowLastSession && isAllowed) {
          isAllowed = opt.value !== 'last-session';
        }

        if (options?.disallowCustomRange && isAllowed) {
          isAllowed = opt.value !== 'custom';
        }
        if (options?.enforceRange && isAllowed) {
          isAllowed = opt.value !== 'last-session';
        }
        if (options?.limitToTwenty && isAllowed) {
          isAllowed = !['custom', 'all-time'].includes(opt.value);
        }
        if (options?.disallowAllTime && isAllowed) {
          isAllowed = opt.value !== 'all-time';
        }

        return isAllowed;
      })
    : defaultInclusiveOptions;
};

/**
 * @description available exclusive date range options to use in date selects
 * @param options
 */
export const exclusiveDateRangeProvider = (
  options?: IDateRangeConfigOptions
): IExclusiveDateOption[] => {
  return !isEmpty(options)
    ? defaultExclusiveOptions.filter((opt: IExclusiveDateOption): boolean => {
        /// default allow
        let isAllowed: boolean = true;
        if (options?.disallowLastSession && isAllowed) {
          isAllowed = opt.value !== 'previous-session';
        }

        if (options?.disallowCustomRange && isAllowed) {
          isAllowed = opt.value !== 'custom';
        }
        if (options?.enforceRange && isAllowed) {
          isAllowed = opt.value !== 'previous-session';
        }
        if (options?.disallowAllTime && isAllowed) {
          isAllowed = opt.value !== 'all-time';
        }

        return isAllowed;
      })
    : defaultExclusiveOptions;
};

export const convertDateOptionValue = (
  option: IExclusiveDateOption | IInclusiveDateOption
): number => {
  switch (option.value) {
    case 'last-session':
    case 'previous-session':
      return 1;
    case 'last-5-sessions':
    case 'previous-5-sessions':
      return 5;
    case 'last-10-sessions':
    case 'previous-10-sessions':
      return 10;
    case 'last-15-sessions':
    case 'previous-15-sessions':
      return 15;
    case 'last-20-sessions':
    case 'previous-20-sessions':
      return 20;
  }
};
